import React, { useEffect, useState } from 'react';

import LoadingSpinner from 'app-migration/components/loading/LoadingSpinner';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react';

import { Button } from 'DesignSystem2/Button';
import { useStores } from 'hooks/use-stores';
import TransfersLoading from 'react_pages/TransfersLoading';
import { fireImpactDeposit } from 'utils/analytics/providers/impact';
import { fireDepositedPostBacks, fireReferralDepositPostBack } from 'utils/analytics/providers/predictionNews';

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
// declare const tatari: any;

interface DepositCardStepProps {
    amount: number;
    onNotNow: (e: React.FormEvent) => void;
    fee?: number;
}

const DepositCardStep = ({ amount, onNotNow, fee = 0 }: DepositCardStepProps) => {
    const { uiStore, traderStore } = useStores();
    const dialog = uiStore.depositDialog;
    const registrationStore = uiStore.registration;
    const userStore = traderStore.user;
    const paymentsStore = traderStore.payments;
    const bankStore = traderStore.bank;

    const [startedFlow, setStartedFlow] = useState(false);

    const fetchDepositsUntilFind = (depositTs: string) => {
        const interval = setInterval(() => {
            bankStore.loadTransfers(userStore.getUserId, () => {
                const deposit = bankStore.transfers.find((transfer) => {
                    if (!transfer.finalizedTs) {
                        return false;
                    }
                    const depositFinalized = DateTime.fromISO(transfer.finalizedTs).toMillis();
                    const depositSuccess = DateTime.fromISO(depositTs!).toMillis();
                    return depositFinalized > depositSuccess;
                });
                if (deposit) {
                    clearInterval(interval);

                    // Track deposit with Impact.com
                    fireImpactDeposit(
                        userStore.getUserId,
                        amount,
                        deposit.id,
                        undefined, // promo code if available
                    ).catch((err) => {
                        console.error('Failed to track Impact debit card deposit:', err);
                    });
                }
            });
        }, 1000);
    };

    useEffect(() => {
        paymentsStore.createCheckoutFlow(
            'deposit',
            amount,
            fee,
            () => setStartedFlow(true),
            () => {
                dialog.setStep('initiated');
                userStore.metrics.recordDepositMetric({
                    step: 'cardDepositSuccess',
                });
                fireDepositedPostBacks(bankStore.depositsCount, amount, uiStore.getPredictionNewsUserId());
                // Also fire referral postback if user has a referral code
                const referralCode = traderStore.user.referrals?.referralInfo?.referral_code;
                if (referralCode) {
                    fireReferralDepositPostBack(referralCode);
                }
                // tatari.track('Deposited', { amount: amount, userId: traderStore.user.getUserId });
                fetchDepositsUntilFind(new Date().toISOString());
            },
            () => {
                dialog.setStep('prereview');
                registrationStore.setFundMethod(undefined);
                uiStore.pushMessage({
                    text: 'Debit card: Something unexpected happened with happened. Please contact support@kalshi.com',
                    variant: 'neutral',
                });
            },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={'flex flex-col gap-3 mt-3 animate-fade-in'}>
            <div className={'flex items-center justify-center h-full min-h-[424px]'}>
                <div
                    id={'flow-container'}
                    className={'-m-[20px]'}
                    style={{ display: !startedFlow || paymentsStore.loadingProviderPayment ? 'none' : undefined }}
                />
                {!startedFlow ? (
                    <LoadingSpinner size={32} />
                ) : (
                    paymentsStore.loadingProviderPayment && <TransfersLoading />
                )}
            </div>
            {!paymentsStore.loadingProviderPayment && (
                <div className={'flex flex-col gap-2'}>
                    <Button
                        variant={'brand'}
                        rank={'secondary'}
                        onClick={(): void => {
                            dialog.setStep('prereview');
                        }}
                        text={'Edit amount'}
                        buttonProps={{ type: 'button' }}
                    />
                    {!!onNotNow && <Button rank={'tertiary'} variant={'brand'} text={'Not now'} onClick={onNotNow} />}
                </div>
            )}
        </div>
    );
};

export default observer(DepositCardStep);
